import { BindersService } from "@/services/api/binders.service";
import { UsersService } from "@/services/api/users.service";
import { CommonModule } from "@angular/common";
import { Component, effect, inject, Input, OnDestroy, signal, ViewChild } from "@angular/core";
import { LazyLoadEvent } from "primeng/api";
import { ChipModule } from "primeng/chip";
import { Table, TableLazyLoadEvent, TableModule } from "primeng/table";
import { GetUserProfileImagePipe } from "@/pipes/get-user-profile-image.pipe";
import { BinderSmall } from "@/models/binder.model";
import { UserTagComponent } from "@/global/user-tag.component";
import { BinderViewService } from "@/services/api/binder-view.service";
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from "primeng/inputtext";
import { SelectModule } from "primeng/select";
import { AppAuthService } from "@/services/app/auth.service";
import { BinderView } from "@/models/binder-view.model";
import { FormsModule } from "@angular/forms";
import { MultiSelectModule } from "primeng/multiselect";
import { ActivityColumnComponent } from "./columns/activity.column.component.ts";
import { TextColumnComponent } from "./columns/text.column.component";
import { EventColumnComponent } from "./columns/event.column.component.ts";
import { MoneyColumnComponent } from "./columns/money.column.component";
import { DraftStatusColumnComponent } from "./columns/draft-status.column.component.ts";
import { ModColumnComponent } from "./columns/mod.column.component.ts";
import { NotesColumnComponent } from "./columns/notes.column.component.ts";
import { PeopleColumnComponent } from "./columns/people.column.component.ts";
import { PersonColumnComponent } from "./columns/person.column.component.ts";
import { StatusColumnComponent } from "./columns/status.column.component.ts";
import { DrawerModule } from 'primeng/drawer';
import { Sort } from "@/models/sort.model";
import { BreakpointService } from "@/layout/service/breakpoint.service.js";
import { DraftingFormComponent } from "./drafting-form.component.js";
import { TabsModule } from 'primeng/tabs';
import { BinderNotesComponent } from "./drafting-notes.component.js";
import { BinderNotesService } from "@/services/api/binder-notes.service.js";
import { SearchMode, SearchService } from "@/services/api/search.service.js";

interface Column {
    id: number,
    field: string;
    header: string;
    type: string;
    default: boolean;
}

interface PrismaSort {
  [key: string]: 'asc' | 'desc';
}

@Component({
    selector: 'app-binder-list',
    template: `
        <div class="card shadow-2 border-round p-2">
            <p-table #dt
                [value]="bindersService.binders()"
                [lazy]="true"
                (onLazyLoad)="onChangeTable($event)"
                [groupRowsBy]="groupBy"
                [columns]="selectedColumns"
                (onRowSelect)="onSelectBinder($event.data)"
                [sortMode]="'multiple'"
                [multiSortMeta]="sortMeta"
                [paginator]="true"
                [rows]="pageSize"
                [selectionMode]="'single'"
                [(selection)]="selectedBinder"
                rowGroupMode="subheader"
                currentPageReportTemplate="Showing {{firstRecord()}} to {{lastRecord()}} of {{bindersService.totalRecords()}} binders"
                [showCurrentPageReport]="true"
                dataKey="BinderID"
                [totalRecords]="bindersService.totalRecords()"
                [loading]="bindersService.loading()"
            >
                <ng-template #caption>
                    <div class="flex">

                        <p-button label="Edit Filters" [outlined]="true" icon="pi pi-filter" class="mr-2"/>
                    <p-multiselect display="chip" styleClass="mr-2" [options]="cols" [(ngModel)]="selectedColumns" optionLabel="header" selectedItemsLabel="{0} columns selected" [style]="{ 'min-width': '200px' }" placeholder="Choose Columns" />
                        <p-select
                        [options]="binderViewService.views()"
                        optionLabel="viewName"
                        (onChange)="onViewChange($event)"
                        [(ngModel)]="selectedView"
                        placeholder="Select View"
                        styleClass="view-dropdown mr-2"
                        />
                        <p-button label="New Prelim" icon="pi pi-plus" (onClick)="onNewPrelim()" class="ml-auto"/>
                    </div>
                </ng-template>
                <ng-template #header>
                    <tr>
                        <th pSortableColumn="BinderID" class="id-column">
                            <div class="flex items-center gap-2">
                            ID
                                <p-sortIcon field="BinderID" />
                            </div>
                        </th>
                        <th *ngFor="let col of selectedColumns" pSortableColumn="col.field" class="name-column">
                            <div class="flex items-center gap-2">
                            {{ col.header }}
                                <p-sortIcon field="ClientLastName" />
                            </div>
                        </th>
                    </tr>
                </ng-template>
                <ng-template #body let-binder let-columns="columns">
                    <tr [pSelectableRow]="binder">
                        <td>{{ binder.BinderID }}</td>
                        <td *ngFor="let col of columns">
                        <ng-container [ngSwitch]="col.type">
                        <app-activity-column *ngSwitchCase="'activity'" [value]="binder.BinderTimeline"></app-activity-column>
                        <app-text-column *ngSwitchCase="'text'" [value]="getValueByPath(binder, col.field)"></app-text-column>
                        <app-event-column *ngSwitchCase="'event'" [value]="binder[col.field]"></app-event-column>
                        <app-money-column *ngSwitchCase="'money'" [value]="binder[col.field]"></app-money-column>
                        <app-draft-status-column *ngSwitchCase="'draftStatus'" [value]="binder[col.field]"></app-draft-status-column>
                        <app-mod-column *ngSwitchCase="'mod'" [value]="binder[col.field]"></app-mod-column>
                        <app-notes-column *ngSwitchCase="'notes'" [value]="binder[col.field]"></app-notes-column>
                        <app-people-column *ngSwitchCase="'people'" [value]="binder[col.field]"></app-people-column>
                        <app-person-column *ngSwitchCase="'person'" [value]="binder[col.field]"></app-person-column>
                          <app-status-column *ngSwitchCase="'status'" [value]="binder[col.field]"></app-status-column>
                          </ng-container>
                        </td>
                    </tr>
                </ng-template>
            </p-table>
        </div>
        <p-drawer [header]="binderHeader" [blockScroll]="true" [(visible)]="binderFormVisible" [position]="isBelowLg() ? 'full' : 'right'" styleClass="w-full lg:!w-[60rem]">
        <p-tabs [value]="formTab">
    <p-tablist>
        <p-tab [value]="'details'">Details</p-tab>
        <p-tab [value]="'notes'">Notes</p-tab>
        <p-tab [value]="'timeline'">Timeline</p-tab>
    </p-tablist>
    <p-tabpanels>
        <p-tabpanel [value]="'details'">
        <app-drafting-form></app-drafting-form>
        </p-tabpanel>
        <p-tabpanel [value]="'notes'">
        <app-binder-notes></app-binder-notes>
        </p-tabpanel>
        <p-tabpanel [value]="'timeline'">
            <p class="m-0">
                At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium voluptatum deleniti atque
                corrupti quos dolores et quas molestias excepturi sint occaecati cupiditate non provident, similique sunt in
                culpa qui officia deserunt mollitia animi, id est laborum et dolorum fuga. Et harum quidem rerum facilis est et
                expedita distinctio. Nam libero tempore, cum soluta nobis est eligendi optio cumque nihil impedit quo minus.
            </p>
        </p-tabpanel>
    </p-tabpanels>
</p-tabs>
          
    </p-drawer>
    `,
    standalone: true,
    imports: [
       TableModule,
       CommonModule,
       ChipModule,
       ButtonModule,
       IconFieldModule,
       InputIconModule,
       InputTextModule,
       SelectModule,
       FormsModule,
       MultiSelectModule,
       DrawerModule,
       DraftingFormComponent,
       TabsModule,
       BinderNotesComponent,
       //columns
       ActivityColumnComponent,
       TextColumnComponent,
       EventColumnComponent,
       MoneyColumnComponent,
       DraftStatusColumnComponent,
       ModColumnComponent,
       NotesColumnComponent,
       PeopleColumnComponent,
       PersonColumnComponent,
       StatusColumnComponent
    ],
    styles: [
        `
                    .view-dropdown {
                min-width: 150px !important;
            }
        :host ::ng-deep .p-datatable-table {

            .id-column {
                width: 50px !important;
            }
            .name-column {
                width: 20% !important;
                max-width: 150px !important;
                min-width: 80px !important;
            }
            .created-column {
                width: 100px !important;
        }
    }
        `
    ]
})

export class BinderListComponent implements OnDestroy {
    public bindersService = inject(BindersService);
    public binderNotesService = inject(BinderNotesService);
    public usersService = inject(UsersService);
    public binderViewService = inject(BinderViewService);
    private authService = inject(AppAuthService);
    private breakpointService = inject(BreakpointService);
    private searchService = inject(SearchService);

    // getUserProfileImage(UID: number) {
    //     return this.usersService.getUserProfileImage(UID)
    //   }

    page: number = 1;
    pageSize: number = 20;

    formTab:string = 'details';

    sortMeta: Sort[] = [{ field: 'BinderID', order: -1 }];

    groupBy: string = 'none';

    cols: Column[] = [
        { id: 1, field: 'ClientLastName', header: 'Job Name', type: 'text', default: true },
        { id: 2, field: 'Users', header: 'Consultant', type: 'person', default: true },
        { id: 3, field: 'binderDrafters', header: 'Drafter', type: 'people', default: true },
        { id: 4, field: 'County', header: 'Jurisdiction', type: 'text', default: true },
        { id: 5, field: 'binderDraftStatus', header: 'Drafting Status', type: 'draftStatus', default: true },
        { id: 6, field: 'Plans.PlanName', header: 'Plan', type: 'text', default: true },
        { id: 7, field: 'ProjectStatus_Binders_ProjectStatusToProjectStatus', header: 'Status', type: 'status', default: true },
        { id: 8, field: 'Lenders.Title', header: 'Lender', type: 'text', default: true },
        { id: 9, field: 'Design_Fee', header: 'Design Fee', type: 'money', default: true },
        { id: 10, field: 'LastActive', header: 'Activity', type: 'activity', default: false },
        { id: 11, field: 'engineeringContacts.coName', header: 'Engineer', type: 'text', default: false },
        { id: 12, field: 'BinderNotes', header: 'Notes', type: 'notes', default: false },
        { id: 13, field: 'ModLevels', header: 'Complexity', type: 'mod', default: false },
        { id: 14, field: '1', header: 'Order Date', type: 'event', default: false },
        { id: 15, field: '2', header: 'Design Prelim Due For Redlines', type: 'event', default: false },
        { id: 16, field: '3', header: 'Design Prelim Sent for Redlines', type: 'event', default: false },
        { id: 17, field: '4', header: 'Design Prelim Due To Office', type: 'event', default: false },
        { id: 18, field: '5', header: 'Design Prelim Complete', type: 'event', default: false },
        { id: 19, field: '6', header: 'NFC Set Approval', type: 'event', default: false },
        { id: 20, field: '7', header: 'NFC Due For Redlines', type: 'event', default: false },
        { id: 21, field: '8', header: 'NFC Sent for Redlines', type: 'event', default: false },
        { id: 22, field: '9', header: 'NFC Due To Office', type: 'event', default: false },
        { id: 23, field: '10', header: 'NFC Complete', type: 'event', default: false },
        { id: 24, field: '11', header: 'Plan Changes Received', type: 'event', default: false },
        { id: 25, field: '12', header: 'Plan Changes Due to Office', type: 'event', default: false },
        { id: 26, field: '13', header: 'Engineering Approval', type: 'event', default: false },
        { id: 27, field: '14', header: 'Original Engineering Redlines Received', type: 'event', default: false },
        { id: 28, field: '15', header: 'Final Review Due', type: 'event', default: false },
        { id: 29, field: '16', header: 'Plan Set Complete', type: 'event', default: false },
        { id: 30, field: '17', header: 'Client Created', type: 'event', default: false },
        { id: 31, field: '18', header: 'Binder Created', type: 'event', default: false },
        { id: 32, field: '19', header: 'Binder Approved', type: 'event', default: false },
        { id: 33, field: '20', header: 'Signature Requested', type: 'event', default: false },
        { id: 34, field: '21', header: 'Office Signed', type: 'event', default: false },
        { id: 35, field: '22', header: 'Corporate Signed', type: 'event', default: false },
        { id: 36, field: '23', header: 'Drafting Order Cancelled', type: 'event', default: false },
        { id: 37, field: 'Franchises_Binders_SelectedFIDToFranchises.CityCode', header: 'Office', type: 'text', default: false },
        { id: 38, field: 'BilledToOffice', header: 'Amount Billed', type: 'money', default: true }
      ];

    selectedColumns!: Column[];

    filter: any = null;
    
    @ViewChild('dt') pTable!: Table;

    private isProgrammaticChange = false;

    constructor() {
      this.searchService.setSearchMode(SearchMode.LIST);
      effect(() => {
          const user = this.authService.userData();
          const views = this.binderViewService.views();
          const selectedView = this.binderViewService.selectedView();
          if (user && views) {
            if (!selectedView) {
              const defaultView = views.find((view) => view.viewId === user.defaultBinderViewId) || views[0];
              this.selectedView = defaultView;
            } else {
              this.loadView();
            }
          }
      });
  }

    get selectedView() {
        return this.binderViewService.selectedView();
    }

    set selectedView(view: BinderView | null) {
        this.binderViewService.selectedView.set(view);
    }
    
    get selectedBinder() {
        return this.bindersService.selectedBinder();
    }
    
    set selectedBinder(binder: BinderSmall | null) {
        this.bindersService.selectedBinder.set(binder);
        this.binderNotesService.getNotes(binder?.BinderID || 0);
        this.formTab = 'details';
    }

    get binderHeader() {
      return 'Binder ' + this.formTab + ' - ' + this.selectedBinder?.ClientLastName || 'New';
    }

    get binderFormVisible() {
      return this.bindersService.binderFormVisible();
    }

    set binderFormVisible(visible: boolean) {
      this.bindersService.binderFormVisible.set(visible);
    }

    isBelowLg() {
        return this.breakpointService.isBelowLg()
    }

      shortenName(name: string) {
        return name.split(' ').map(n => n[0]).join('.');
      }

      getValueByPath(obj: any, path: string) {
        if (!obj || !path) return '';
        
        const result = path.split('.').reduce((o, p) => {
          if (o === null || o === undefined) return undefined;
          return o[p];
        }, obj);
        
        // Return empty string if result is null, undefined, or an object
        if (result === null || result === undefined) return '';
        if (typeof result === 'object') return '';
        
        return result;
      }

    //   lazyLoad(event: TableLazyLoadEvent) {
    //     this.bindersService.loadItemsLazy(event, this.mode);
    //   }

      getLenderName(binder: BinderSmall) {
        return binder.Lenders?.Title || 'N/A';
      }

      firstRecord() {
        return this.page * this.pageSize + 1;
      }
      
      lastRecord() {
        const possibleLast = (this.page + 1) * this.pageSize;
        const total = this.bindersService.totalRecords();
        return possibleLast > total ? total : possibleLast;
      }

      onSelectBinder(binder: BinderSmall) {
        this.binderFormVisible = true;
        this.bindersService.setSelectedBinder(binder);
        console.log('onSelectBinder', binder);
      }

      getPositionMessage() {
        return `Showing ${this.firstRecord()} to ${this.lastRecord()} of ${this.bindersService.totalRecords()} entries`;
      }

      onViewChange(event: any) {
        this.selectedView = event.value;
        console.log('selectedView', this.selectedView);
    }

    // This method now only gets called once per user action
    // It updates the table's state, which in turn triggers onLazyLoad
    loadView() {
        if (!this.selectedView) return;

        this.isProgrammaticChange = true;

        // Parse and set the properties
        this.sortMeta = this.parsePrismaSort(this.selectedView.orderBy);
        this.pageSize = this.selectedView.defaultPageSize;
        this.changeColumns(this.selectedView);
        this.filter = this.parseFilter(this.selectedView.whereConditions);
        this.page = 1; // Reset to page 1

        // Use setTimeout to ensure PrimeNG's internal state updates before the next check
        setTimeout(() => {
            this.pTable.first = 0;
            this.pTable.rows = this.pageSize;
            this.pTable.multiSortMeta = this.sortMeta;
            this.pTable.onLazyLoad.emit({
                first: 0,
                rows: this.pageSize,
                sortField: this.sortMeta && this.sortMeta.length > 0 ? this.sortMeta[0].field : null,
                sortOrder: this.sortMeta && this.sortMeta.length > 0 ? this.sortMeta[0].order : 1,
                multiSortMeta: this.sortMeta,
                filters: this.filter,
            });
        });
    }

    // The handler for the p-table's onLazyLoad event
    onChangeTable(event: TableLazyLoadEvent) {
        if (this.isProgrammaticChange) {
            this.isProgrammaticChange = false;
            return;
        }

        console.log('onChangeTable', event);

        // Update local state based on the event
        this.page = event.first! / event.rows! + 1;
        this.pageSize = event.rows!;
        this.sortMeta = event.multiSortMeta || [];
        this.filter = event.filters || null;

        // Call your data service with the new state
        if(this.selectedView) {
        const viewId = this.selectedView!.viewId || null;
        if(viewId) {
          console.log('view id', viewId);
        this.bindersService.loadViewLazy(this.page, this.pageSize, viewId, this.sortMeta, this.filter);
        } else {
            console.log('no view id');
          }
        }
      }

      changeColumns(view: BinderView) {
        if (view) {
          // Parse the colString into an array
          const colArray = JSON.parse(view.colString);
          if (view.groupBy) {
            this.groupBy = view.groupBy
          } else {
            this.groupBy = 'none'
          }
      
          // Filter cols based on the colArray
          this.selectedColumns = this.cols.filter(col => colArray.includes(col.id));
        }
      }

      showSort(event: any) {
        console.log('showSort', event);
      }

      setSortField(sort: string) {
        this.sortMeta = this.parsePrismaSort(sort);
      }
      
      parsePrismaSort(sort: string): { field: string, order: number }[] {
        try {
          const parsed = JSON.parse(sort) as Array<{ [key: string]: 'asc' | 'desc' }>;
      
          return parsed.map(obj => {
            const [field, direction] = Object.entries(obj)[0];
            return {
              field,
              order: direction.toLowerCase() === 'asc' ? 1 : -1
            };
          });
        } catch (err) {
          console.error('Invalid sort string:', err);
          return [];
        }
      }
      onNewPrelim() {
        this.binderFormVisible = true;
        this.bindersService.setSelectedBinder(null);
        console.log('onNewPrelim');
      }

      parseFilter(filter: string): any {
        try {
          const object = JSON.parse(filter);
          return object;
        } catch (err) {
          console.error('Invalid filter string:', err);
          return null;
        }
      }
      
      ngOnDestroy() {
        this.searchService.setSearchMode(SearchMode.ALL);
      }
}